import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

// NOTE: Importing from specific files instead of the '../agent' barrel to avoid
// a circular dependency: agent.ts → ../admin → access.ts → ../agent → agent.ts.
// The '../agent' barrel re-exports agent.ts which imports from '../admin',
// creating the cycle. Importing the specific files breaks it.
import * as _agent from '../agent/agent'
import type { ZoneConfig as _ZoneConfig } from '../agent/zone-config'
import type * as common from '../common'

import * as app from './app'
import type { Context } from './context'
import * as scenario from './scenario'
import * as tenant from './tenant'
import * as user from './user'
import * as zone from './zone'

/**
 * A number that bitwise encodes permissions, each represented by an
 * {@link Access.Flag}
 */
export type Access = number
export namespace Access {
  export const isAccess = TypeChecker.isNumber.usingAlias('Access')

  /**
   * Creates a new {@link Access} from a series of {@link Access.Flag}
   *
   * @see {@link union}
   */
  export function from(...flags: readonly Flag[]): Access {
    return union(...flags)
  }

  /**
   * Checks if each of the given {@link Flag} are enabled in the given
   * {@link Access}
   */
  export function allows(
    access: Access | undefined,
    ...flags: readonly Flag[]
  ): boolean {
    // eslint-disable-next-line no-bitwise
    return access !== undefined && flags.every(flag => (access & flag) === flag)
  }

  /**
   * Checks if at least one of the given {@link Flag} are enabled in the given
   * {@link Access}
   */
  export function allowsAtLeastOne(
    access: Access | undefined,
    ...flags: readonly Flag[]
  ): boolean {
    // eslint-disable-next-line no-bitwise
    return access !== undefined && flags.some(flag => (access & flag) === flag)
  }

  /**
   * Checks whether tenant-level access grants entry to Tenant Admin Portal.
   *
   * Only tenant assignments should use this check. App, zone, scenario, and
   * agent assignments may carry some of the same flags for Main App behavior,
   * but they do not grant Tenant Admin Portal access.
   */
  export function grantsTenantAdminPortalAccess(
    access: Access | undefined,
  ): boolean {
    return allowsAtLeastOne(
      access,
      Flag.CanManageUsers,
      Flag.CanConfigureInfo,
      Flag.CanCreateChildren,
      Flag.CanDeleteUnownedChildren,
    )
  }

  /**
   * Returns a new {@link Access} which has all of the permissions of the
   * given {@link Access} plus the additional {@link Flag flags} that were provided
   */
  export function add(access: Access, ...flags: readonly Flag[]): Access {
    return union(access, ...flags)
  }

  /**
   * Returns a new {@link Access} which has the permissions of the given
   * {@link Access} except those corresponding to the {@link Flag flags} that were
   * provided
   */
  export function remove(access: Access, ...flags: readonly Flag[]): Access {
    return intersection(access, ...flags.map(flag => ~flag))
  }

  /**
   * Returns a new {@link Access} that has all permissions that _any_ of the
   * given {@link Access} have
   */
  export function union(...accesses: readonly Access[]): Access {
    let access: Access = 0
    for (const _access of accesses) {
      // eslint-disable-next-line no-bitwise
      access |= _access
    }
    return access
  }

  /**
   * Returns a new {@link Access} that only has the permissions that _all_ of
   * the given {@link Access} have
   */
  export function intersection(...accesses: readonly Access[]): Access {
    if (accesses.length === 0) {
      return 0
    }

    // Start with the first access value
    let result: Access = accesses[0]

    // Apply bitwise AND with each subsequent access
    for (let i = 1; i < accesses.length; i++) {
      // eslint-disable-next-line no-bitwise
      result &= accesses[i]
    }

    return result
  }

  /**
   * Represents different permissions that a user can have in relation to a
   * given item
   */
  export enum Flag {
    /**
     * Allows standard Main App use of the assigned item.
     *
     * Main App:
     *
     * - Grants access to the tenants, zones, apps, scenarios, and agents a
     *   user is assigned to.
     * - On zone assignments, authorizes DMS record mutations in that zone.
     * - On DMS app assignments, authorizes DMS schema and file operations.
     * - On Mosaic app assignments, authorizes page, widget, and action
     *   management.
     *
     * Tenant Admin Portal:
     *
     * - Does not grant access to Tenant Admin Portal by itself.
     *
     * Assignment modal availability:
     *
     * - Available in both Platform Admin Portal and Tenant Admin Portal for all
     *   supported assignment levels.
     */
    CanUse = 1,
    /**
     * Allows tenant-level user management.
     *
     * Main App:
     *
     * - Has no direct effect.
     *
     * Tenant Admin Portal:
     *
     * - Grants access to Tenant Admin Portal for the selected tenant.
     * - Allows creating tenant-level users.
     * - Allows assigning and unassigning users for the tenant, zones, apps,
     *   scenarios and agents.
     *
     * Assignment modal availability:
     *
     * - Available only in Platform Admin Portal on tenant-level assignments.
     */
    CanManageUsers = 2,
    /**
     * Allows editing basic entity information.
     *
     * Main App:
     *
     * - Has no direct effect.
     *
     * Tenant Admin Portal:
     *
     * - Grants access to Tenant Admin Portal for the selected tenant.
     * - Allows editing general information for the tenant and its zones and
     *   apps.
     *
     * Assignment modal availability:
     *
     * - Available only in Platform Admin Portal on tenant-level assignments.
     */
    CanConfigureInfo = 4,
    /**
     * Allows configuring agent and app functionality.
     *
     * Main App:
     *
     * - For agents, makes the agent settings action available from the
     *   Assistant slideout.
     * - On app assignments that support functionality configuration,
     *   authorizes the app's configuration reads and changes.
     *
     * Tenant Admin Portal:
     *
     * - Does not grant access to Tenant Admin Portal by itself.
     *
     * Assignment modal availability:
     *
     * - Available in both portals for user-to-agent assignments from tenant,
     *   zone, and app, and user-to-app assignments where the app supports
     *   functionality configuration.
     */
    CanConfigureFunctionality = 8,
    /**
     * Allows creating child entities.
     *
     * Main App:
     *
     * - On scenario-supporting app assignments, allows creating scenarios and
     *   editing or deleting scenarios created by the same user.
     *
     * Tenant Admin Portal:
     *
     * - Grants access to Tenant Admin Portal for the selected tenant.
     * - Allows creating zones, apps, scenarios, and app pages for the selected
     *   tenant.
     * - Allows editing and deleting children created by the same user.
     *
     * Assignment modal availability:
     *
     * - Available in Platform Admin Portal on tenant-level assignments.
     * - Available in both Platform Admin Portal and Tenant Admin Portal on app
     *   assignments for apps that support scenarios.
     */
    CanCreateChildren = 16,
    /**
     * Allows deleting children of the associated item that are not owned by
     * this user (a user can always delete the children that they created/own)
     *
     * Main App:
     *
     *
     * - On scenario-supporting app assignments, allows deleting scenarios that
     *   were created by users other than the current user.
     *
     * Tenant Admin Portal:
     *
     * - Grants access to Tenant Admin Portal for the selected tenant.
     * - Allows deleting zones, apps, scenarios, and app pages created by other
     *   users.
     *
     * Assignment modal availability:
     *
     * - Available in Platform Admin Portal on tenant-level assignments.
     * - Available in both Platform Admin Portal and Tenant Admin Portal on app
     *   assignments for apps that support scenarios.
     */
    CanDeleteUnownedChildren = 32,
    /**
     * Allows minting API keys that call the REST API as this user, scoped to
     * the assigned zone.
     *
     * A key can never exceed the permissions its owner already has — those are
     * still enforced downstream on every request — so this only controls who
     * may create a long-lived credential at all.
     *
     * Main App:
     *
     * - Makes the API Access slideout available from the navbar, where the
     *   user can list, generate, and revoke their own keys for the current
     *   zone.
     *
     * Tenant Admin Portal:
     *
     * - Does not grant access to Tenant Admin Portal by itself.
     *
     * Assignment modal availability:
     *
     * - Available in Platform Admin Portal on tenant-level assignments (where
     *   it applies to every zone in the tenant).
     * - Available in both Platform Admin Portal and Tenant Admin Portal on
     *   zone assignments.
     */
    CanGenerateApiKey = 64,
  }
  // Needs to be before namespace is defined
  const FLAGS: ReadonlySet<Flag> = new Set(
    Object.values(Flag).filter(TypeChecker.isNumber) as Flag[],
  )
  export namespace Flag {
    export const ALL: readonly Flag[] = Array.from(FLAGS)
    export const isFlag = TypeChecker.Factory.oneOf(FLAGS)
    export const assertIsFlag: TypeAsserter<Flag> =
      TypeAsserter.Factory.from(isFlag)
    export interface WithLabel {
      key: string
      label: string
      value: Flag
    }
    export namespace WithLabel {
      /** Utility function to convert PascalCase to readable labels */
      function enumKeyToLabel(key: string): string {
        return key
          .replace(/([A-Z])/g, ' $1') // Add space before capital letters
          .trim() // Remove leading space
      }

      /** Get all Flag enum keys with their labels and values */
      export function get(): WithLabel[] {
        return Object.entries(Flag)
          .filter(([_, value]) => typeof value === 'number') // Keep only enum values
          .map(([key, value]) => ({
            key,
            label: enumKeyToLabel(key),
            value: value as Flag,
          }))
      }
    }
  }

  /**
   * Contains some default {@link Access} to use when assigning an item to a
   * user
   */
  export namespace Default {
    /**
     * Default {@link Access} granted to the owner of an item
     */
    export const FOR_OWNER: Access = add(
      Flag.CanUse,
      Flag.CanManageUsers,
      Flag.CanConfigureInfo,
      Flag.CanCreateChildren,
      Flag.CanDeleteUnownedChildren,
    )
  }

  /**
   * Anything that has an associated {@link Access}
   */
  export interface Holder {
    /** @see {@link Access} */
    access: Access
  }

  /**
   * Used to assign {@link Access} of various things to various things
   *
   * @see {@link Assignment.Type}, {@link Assignment.For},
   * {@link Assignment.Strict}
   *
   * TODO Assignment should extends {@link common.Tagged}
   */
  export interface Assignment extends Context.Require<'userId'>, Holder {
    /** @see {@link _agent.Agent.Id} */
    agentId?: _agent.Agent.Id
    /** @see {@link _ZoneConfig.PageContext} */
    pageContext?: _ZoneConfig.PageContext
    // TODO: Find a better place where this properties should be defined
    excludePages?: Set<app.App.Page.Id>
    includePages?: Set<app.App.Page.Id>
  }
  export namespace Assignment {
    /**
     * {@link Assignment} can be used to assign {@link Access} in various ways.
     * This represents each of those ways
     */
    export enum Type {
      /** @see {@link For.Tenant} */
      Tenant = 'tenant',
      /** @see {@link For.Zone} */
      Zone = 'zone',
      /** @see {@link For.App} */
      App = 'app',
      /** @see {@link For.Scenario} */
      Scenario = 'scenario',
      /** @see {@link For.Agent} */
      Agent = 'agent',
    }
    // Needs to be before namespace is defined
    const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
    export namespace Type {
      export const ALL: readonly Type[] = Array.from(TYPES)
      export const isType = TypeChecker.Factory.oneOf(TYPES)
      export const assertIsType: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isType)

      /**
       * Identifies the {@link Type} of the given {@link Assignment}
       *
       * Returns `undefined` if the {@link Assignment} is not valid (i.e. does
       * not match {@link Strict})
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from(assignment: Readonly<Assignment>): Type | undefined {
        // NOTE: Agent check must come first because an agent assignment also has
        // `tenantId` + `zoneId` (and optionally `appId`) but is distinguished
        // by the presence of `agentId`.
        if (assignment.agentId !== undefined) {
          return Type.Agent
        }
        return ALL.find(type => For.getTypeChecker(type)(assignment))
      }
    }

    /**
     * Union of valid versions of {@link Assignment}
     *
     * @see {@link For} for the individual members of this union
     */
    export type Strict = For<Type>
    export namespace Strict {
      export const isStrict: TypeChecker<Strict> = TypeChecker.Factory.lazy(
        () => TypeChecker.Factory.union(...Object.values(STRICT_TYPE_CHECKERS)),
      ).usingAlias('Access.Assignment.Strict')
    }

    /**
     * Valid {@link Assignment} for the given {@link Type}
     *
     * This is also a namespace containing a separate type for each valid
     * {@link Assignment}
     */
    export type For<TType extends Type> = {
      [Type.Agent]: For.Agent
      [Type.App]: For.App
      [Type.Zone]: For.Zone
      [Type.Scenario]: For.Scenario
      [Type.Tenant]: For.Tenant
    }[TType]
    export namespace For {
      /**
       * Gets the runtime checker for the assignment shape associated with the
       * given {@link Type}.
       */
      export function getTypeChecker<TType extends Type>(
        type: TType,
      ): TypeChecker<For<TType>> {
        return STRICT_TYPE_CHECKERS[type]
      }

      /**
       * Requires everything that is required in the given {@link Context} AND
       * prohibits anything that is not required
       */
      type Using<TContext extends Context> = Pick.Exactly<
        // Requires everything that is picked
        Required<Assignment>,
        // Picks keys of Assignment that are required in either Assignment or
        // TContext
        Extract<keyof Assignment, keyof Pick.Required<Assignment & TContext>>
      >

      /**
       * An {@link Assignment} modifying the {@link Access} a {@link user.User} has to
       * a {@link tenant.Tenant}
       *
       * @see {@link Context.ForTenant}
       */

      export type Tenant = Using<Context.ForTenant>
      export namespace Tenant {
        export const isTenant: TypeChecker<Tenant> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.object<Tenant>({
              access: Access.isAccess,
              agentId: TypeChecker.isUndefined,
              appId: TypeChecker.isUndefined,
              excludePages: TypeChecker.isUndefined,
              includePages: TypeChecker.isUndefined,
              pageContext: TypeChecker.isUndefined,
              scenarioId: TypeChecker.isUndefined,
              tenantId: tenant.Tenant.Id.isId,
              userId: user.User.Id.isId,
              zoneId: TypeChecker.isUndefined,
            }),
        ).usingAlias('Access.Assignment.For.Tenant')
      }

      /**
       * An {@link Assignment} modifying the {@link Access} a {@link user.User} has to
       * a {@link zone.Zone} within a given {@link tenant.Tenant}
       *
       * @see {@link Context.ForZone}
       */
      export type Zone = Using<Context.ForZone>
      export namespace Zone {
        export const isZone: TypeChecker<Zone> = TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<Zone>({
            access: Access.isAccess,
            agentId: TypeChecker.isUndefined,
            appId: TypeChecker.isUndefined,
            excludePages: TypeChecker.isUndefined,
            includePages: TypeChecker.isUndefined,
            pageContext: TypeChecker.isUndefined,
            scenarioId: TypeChecker.isUndefined,
            tenantId: tenant.Tenant.Id.isId,
            userId: user.User.Id.isId,
            zoneId: zone.Zone.Id.isId,
          }),
        ).usingAlias('Access.Assignment.For.Zone')
      }

      /**
       * An {@link Assignment} modifying the {@link Access} a {@link user.User}
       * has to an {@link app.App} within a {@link zone.Zone} in a given
       * {@link tenant.Tenant}
       *
       * @see {@link Context.ForApp}
       */
      export type App = Using<Context.ForApp>
      export namespace App {
        export const isApp: TypeChecker<App> = TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<App>({
            access: Access.isAccess,
            agentId: TypeChecker.isUndefined,
            appId: app.App.Id.isId,
            // TODO: Change Typechecker here to be
            // TypeChecker.Factory.optional(TypeChecker.isSet), after figuring
            // out issue with type complaining
            excludePages: TypeChecker.Factory.optional(TypeChecker.isAny),
            includePages: TypeChecker.Factory.optional(TypeChecker.isAny),
            pageContext: TypeChecker.isUndefined,
            scenarioId: TypeChecker.isUndefined,
            tenantId: tenant.Tenant.Id.isId,
            userId: user.User.Id.isId,
            zoneId: zone.Zone.Id.isId,
          }),
        ).usingAlias('Access.Assignment.For.App')
      }

      /**
       * An {@link Assignment} modifying the {@link Access} a {@link user.User}
       * has to a {@link scenario.Scenario} within an {@link app.App} in a given
       * {@link zone.Zone} in a given {@link tenant.Tenant}
       *
       * @see {@link Context.ForScenario}
       */
      export type Scenario = Using<Context.ForScenario>
      export namespace Scenario {
        export const isScenario: TypeChecker<Scenario> =
          TypeChecker.Factory.lazy(() =>
            TypeChecker.Factory.object<Scenario>({
              access: Access.isAccess,
              agentId: TypeChecker.isUndefined,
              appId: app.App.Id.isId,
              excludePages: TypeChecker.isUndefined,
              includePages: TypeChecker.isUndefined,
              pageContext: TypeChecker.isUndefined,
              scenarioId: scenario.Scenario.Id.isId,
              tenantId: tenant.Tenant.Id.isId,
              userId: user.User.Id.isId,
              zoneId: zone.Zone.Id.isId,
            }),
          ).usingAlias('Access.Assignment.For.Scenario')
      }

      /**
       * An {@link Assignment} assigning agent access to a {@link user.User}.
       * Agent assignments span multiple scope levels (tenant, zone, app)
       * and are distinguished by the presence of `agentId`.
       */
      export type Agent = Pick.Exactly<
        Assignment,
        | 'userId'
        | 'tenantId'
        | 'zoneId'
        | 'appId'
        | 'agentId'
        | 'access'
        | 'pageContext'
      >
      export namespace Agent {
        export const isAgent: TypeChecker<Agent> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.object<Agent>({
              access: Access.isAccess,
              agentId: _agent.Agent.Id.isId as TypeChecker<Agent['agentId']>,
              appId: TypeChecker.Factory.optional(app.App.Id.isId),
              excludePages: TypeChecker.isUndefined,
              includePages: TypeChecker.isUndefined,
              pageContext: TypeChecker.Factory.optional(TypeChecker.isAny),
              scenarioId: TypeChecker.isUndefined,
              tenantId: TypeChecker.Factory.optional(tenant.Tenant.Id.isId),
              userId: user.User.Id.isId,
              zoneId: TypeChecker.Factory.optional(zone.Zone.Id.isId),
            }),
        ).usingAlias('Access.Assignment.For.Agent')
      }
    }
    // NOTE: must appear after all of these TypeCheckers have been defined
    const STRICT_TYPE_CHECKERS: {
      [TType in Type]: TypeChecker<For<TType>>
    } = {
      [Type.Agent]: For.Agent.isAgent,
      [Type.App]: For.App.isApp,
      [Type.Zone]: For.Zone.isZone,
      [Type.Scenario]: For.Scenario.isScenario,
      [Type.Tenant]: For.Tenant.isTenant,
    }
  }
}
